import { ref } from 'vue';
import { useToast } from 'vue-toastification';
import apiClient from '@/api/apiClient';
import { t } from '@/i18n';

const ENDPOINTS = {
    xlsx: '/export-expenses.php',
    pdf: '/export-expenses-pdf.php',
};

// Downloads the expenses matching the given filters as an Excel or PDF file. Filters are the
// same object shape used by useExpensesList (startDate, endDate, selectedSubcategory,
// recurringFilter, searchTerm).
export function useExpensesExport() {
    const toast = useToast();
    const exporting = ref(false);

    const exportExpenses = (filters, format = 'xlsx') => {
        exporting.value = true;

        // Axios would otherwise serialize a null startDate/endDate as the literal string "null"
        // in the query string, which the backend would then treat as a (bogus) date filter
        const params = {
            selectedSubcategory: filters.selectedSubcategory ?? 'all',
            recurringFilter: filters.recurringFilter,
            searchTerm: filters.searchTerm,
        };
        if (filters.startDate) params.startDate = filters.startDate;
        if (filters.endDate) params.endDate = filters.endDate;

        return apiClient.get(ENDPOINTS[format], {
            params,
            responseType: 'blob',
        })
            .then((response) => {
                const filename = filenameFromResponse(response) || `expenses.${format}`;
                downloadBlob(response.data, filename);
            })
            .catch(async (error) => {
                console.error('Error exporting expenses:', error);
                toast.error(t('export.failed', { detail: await blobErrorMessage(error) }));
            })
            .finally(() => {
                exporting.value = false;
            });
    };

    return { exporting, exportExpenses };
}

// With responseType 'blob', a JSON error body (401, 500...) arrives as a Blob instead of parsed
// JSON, so the usual getErrorMessage() can't read it. Read the blob as text and parse it instead.
async function blobErrorMessage(error) {
    const data = error.response && error.response.data;
    if (data instanceof Blob) {
        try {
            const parsed = JSON.parse(await data.text());
            if (parsed && parsed.message) {
                return parsed.message;
            }
        } catch (parseError) {
            // Not a JSON blob (e.g. a network error): fall through to the generic message
        }
    }
    return t('common.genericError');
}

// Pulls the filename out of Content-Disposition (e.g. 'attachment; filename="despesas-2026-09.xlsx"')
function filenameFromResponse(response) {
    const header = response.headers['content-disposition'] || '';
    const match = header.match(/filename="?([^"]+)"?/);
    return match ? match[1] : null;
}

function downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}
